import React, { useContext, useEffect } from 'react';

import addSvg from '@assets/img/icons/add.svg';
import deleteSvg from '@assets/img/icons/delete-gray.svg';
import { TextInput } from '@components/NextForms/TextInput';
import { PriceSimulatorFormContext } from '@components/utilities/PriceSimulator/helpers';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { contactStrings } from '@data/contacts';
import { useCNZipCodeAutoComplete } from '@hooks/next/useZipCodeAutoComplete';
import { empty, getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';
import { observer } from 'mobx-react';

const strings = createStrings({
  ja: {
    inputPickupAddressPrompt:
      '中国国内の都市名(英字名)と郵便番号のいずれか一つを入力して、現れた選択肢中から一つを選んでください',
    addPickupAddress: `${bookingInquiryStrings.pickupAddress}を追加する`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  choicesContainer: {
    padding: '1rem',
  },
  priceSimulatorPickupAddressInputContainer: {
    maxWidth: 700,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr 1fr auto',
    rowGap: 8,
    columnGap: 16,
    marginTop: '0.5rem',
  },
  priceSimulatorPickupAddressInputWrapper: {
    ...common.textGray700,
    ...common.middleText,
    ...common.flexColumn,
    ...common.justifyBetween,
    height: '100%',
  },
  addPickupAddressContainer: {
    ...common.baseButton,
    ...common.textPrimary,
    ...common.fontWeight600,
    borderTop: `1px solid ${Color(theme.palette.primary.main).alpha(0.2).toString()}`,
    padding: '0.75rem 1rem',
  },
}));

const PickupAddressInputRow: React.FC<{
  index: number;
  onDelete: () => void;
  canDelete: boolean;
}> = observer(({ index, onDelete, canDelete }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { form } = useContext(PriceSimulatorFormContext);
  const { fields } = form.root;
  const pickupAddressField = fields.pickup_addresses.elements[index];
  const { fields: itemFields } = pickupAddressField;

  if (!pickupAddressField) {
    throw new Error(`pickup_addresses does not have index ${index}`);
  }

  const autoCompleteOptions = useCNZipCodeAutoComplete({
    zipcodeField: itemFields.zip_code,
    address1Field: itemFields.city_name,
    address1CNField: itemFields.dummy_address_cn,
  });

  return (
    <>
      <div
        className={clsx(classes.textPrimary, classes.fontWeight600Important)}
        style={{ paddingTop: 6 }}
      >
        {bookingInquiryStrings.pickupAddress} {index + 1}
      </div>
      <div>
        <TextInput
          field={itemFields.city_name}
          inputProps={{ placeholder: contactStrings.city_name }}
          autoCompleteConfig={autoCompleteOptions.address1}
          popperConfig={{
            width: 'max-content',
          }}
        />
      </div>
      <div>
        <TextInput
          field={itemFields.zip_code}
          inputProps={{ placeholder: contactStrings.zip_code }}
          autoCompleteConfig={autoCompleteOptions.zipcode}
          popperConfig={{
            width: 'max-content',
          }}
        />
      </div>

      {canDelete ? (
        <button style={{ alignSelf: 'start' }} onClick={onDelete}>
          <img src={deleteSvg} alt="Delete" style={{ position: 'relative', top: 4 }} />
        </button>
      ) : (
        <div />
      )}
    </>
  );
});

export const PriceSimulatorPickupAddressInput: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { form } = useContext(PriceSimulatorFormContext);
  const { fields } = form.root;

  useEffect(() => {
    if (fields.pickup_addresses.elements.length === 0) {
      fields.pickup_addresses.push(empty);
    }
  }, []);

  return (
    <div className={classes.priceSimulatorPickupAddressInputWrapper}>
      <div className={classes.choicesContainer}>
        <div>{strings.inputPickupAddressPrompt}</div>

        <div className={classes.priceSimulatorPickupAddressInputContainer}>
          {fields.pickup_addresses.elements.map((field, index) => (
            <PickupAddressInputRow
              key={field.uniqueId}
              index={index}
              onDelete={() => {
                fields.pickup_addresses.splice(index, 1, []);

                fields.cargos.elements.forEach((cargo) => {
                  if (!cargo.fieldsResult.success) {
                    return;
                  }

                  const pickup_address_index = getDecodeResult(
                    cargo.fieldsResult.fields.pickup_address_index.decodeResult,
                  );

                  if (pickup_address_index !== undefined && pickup_address_index !== 0) {
                    const realIndex = pickup_address_index - 1;

                    if (realIndex === index) {
                      cargo.fieldsResult.fields.pickup_address_index.setOutput(0);
                    } else if (realIndex > index) {
                      cargo.fieldsResult.fields.pickup_address_index.setOutput(
                        pickup_address_index - 1,
                      );
                    }
                  }
                });
              }}
              canDelete={fields.pickup_addresses.length > 1}
            />
          ))}
        </div>
      </div>

      <div
        className={classes.addPickupAddressContainer}
        onClick={() => fields.pickup_addresses.push(empty)}
      >
        <img
          src={addSvg}
          alt="Add"
          style={{ height: 16, width: 16, position: 'relative', top: 3, marginRight: 8 }}
        />

        {strings.addPickupAddress}
      </div>
    </div>
  );
});
